import { createReducer } from 'lib/utilities';
import { formatUserSales } from '../../middleware/reportingUtilities.js';
import sortByDateProp from '../../lib/sortByDateProp.js';
import { prettyCurrency } from '../../lib/numUtils';

const cleanPayload = (payload) => {
  return payload === null || (Array.isArray(payload) && !payload.length) ? null : payload;
};

const initialState = {
  reportingGraphsLoadState: {},
  reportingSalesDetailsDisablePagination: false,
  triggerFetchReports: false,
  salesReports: [],
  leadReports: [],
  leadsPieGraph: null,
  leadsInquiryTypesPie: [],
  salesDetails: [],
  salesDetailsTotal: undefined,
  leadsDetails: [],
  totalsBarLeads: null,
  activityReport: null,
  leadLoopReports: [],
  totalsBarSales: [
    {
      report_date: 'Totals',
      status: 'Active',
      pipeline_count: '0',
      service_total_value: '0',
    },
    {
      report_date: 'Totals',
      status: 'Won',
      pipeline_count: '0',
      service_total_value: '0',
    },
    {
      report_date: 'Totals',
      status: 'Lost',
      pipeline_count: '0',
      service_total_value: '0',
    },
  ],
  utmReports: [],
  utmSources: [],
  utmCampaigns: [],
  reasonsLost: [],
  salesStackedUserKeys: [],
  salespersonSalesWonPieGraph: [],
  salesStackedUsersData: [],
  salesUtmMediumPie: [],
  salesUTMSource: [],
  salesUTMCampaign: [],
  salesLostSalesperson: [],
  salesLostByUTMMedium: [],
  reportingLeadsDetailsTotal: '0',
  milestoneFunnelGraphData: [],
  activePipelineGraphData: [],
  graphHoverState: false,
};

export default createReducer(initialState, {
  GET_LEAD_SUMMARY_REPORT_SUCCESS: (state, payload) => {
    const data = payload.data || {};
    const dates = {};
    (data.lead_type_graph_unique || []).forEach((n) => {
      dates[n.report_date] = {
        ...(dates[n.report_date] || {}),
        Calls: n.type === 'Call' ? parseFloat(n.report_count) : (dates[n.report_date] || {}).Calls || 0,
        Texts: n.type === 'Text' ? parseFloat(n.report_count) : (dates[n.report_date] || {}).Texts || 0,
        Forms: n.type === 'Form' ? parseFloat(n.report_count) : (dates[n.report_date] || {}).Forms || 0,
      };
    });
    (data.good_lead_graph || []).forEach((n) => {
      dates[n.report_date] = {
        ...(dates[n.report_date] || {}),
        ['Good Inquiry']: parseFloat(n.report_count),
      };
    });
    const leadReports = Object.entries(dates)
      .map((n) => ({ key: n[0], value: n[1] }))
      .map((n) => ({
        ...n.value,
        'Report Date': String(n.key).slice(5).split('-').join('/'),
      }));

    return {
      ...state,
      leadReports,
      utmReports: (data.utm_medium_total || []).map((n) => ({
        lead_count: parseFloat(n.report_count),
        value: parseFloat(n.report_count),
        name: n.utm_medium_name,
      })),
      utmCampaigns: (data.utm_campaign_total || []).map((n) => ({
        label: n.utm_campaign,
        value: parseFloat(n.report_count),
      })),
      utmSources: (data.utm_source_total || []).map((n) => ({
        label: n.utm_source_name || n.utm_source,
        value: parseFloat(n.report_count),
      })),
      leadsInquiryTypesPie: (data.lead_type_totals || []).map((n) => ({
        name: n.type,
        value: parseFloat(n.report_count),
      })),
      leadsPieGraph: [
        {
          name: 'Not a inquiry',
          value: parseFloat(((data.good_lead_total || []).find((n) => n.good_lead === '0') || {}).report_count || 0),
        },
        {
          name: 'Good Inquiry',
          value: parseFloat(((data.good_lead_total || []).find((n) => n.good_lead === '1') || {}).report_count || 0),
        },
      ],
      totalsBarLeads:
        data.lead_type_totals.length < 1
          ? null
          : [
              { name: 'Leads', value: (data.lead_total[0] || {}).total_row_count },
              ...data.lead_type_totals.map((n) => ({
                name:
                  {
                    Call: 'Calls',
                    Text: 'Texts',
                    Form: 'Forms',
                  }[n.type] || n.type,
                value: parseFloat(n.report_count),
              })),
            ],
    };
  },
  GET_LEADLOOP_REPORTS: (state, payload) => ({
    ...state,
    leadLoopReports: payload,
  }),
  GET_ACTIVITY_REPORT: (state, payload) => ({
    ...state,
    activityReport: payload,
  }),
  SET_GRAPH_LOAD_STATE: (state, payload) => ({
    ...state,
    reportingGraphsLoadState: { ...state.reportingGraphsLoadState, ...payload },
  }),
  SET_TRIGGER_REPORTING_FETCH: (state, payload) => ({
    ...state,
    triggerFetchReports: payload,
  }),
  FETCH_MILESTONE_GRAPH_DATA_SUCCESS: (state, payload) => ({
    ...state,
    milestoneFunnelGraphData: payload.data,
  }),

  GET_SALES_REPORT_SUMMARY_SUCCESS: (state, payload) => {
    const data = payload.data || [];
    const flattened = (data || []).flat();
    // Totals bar
    const totalsBarData = (flattened || []).filter((n) => n.report_type === 'sales_status_totals').map((n) => ({ ...n, report_date: 'Totals', pipeline_count: n.report_count, service_total_value: n.report_value }));
    // User sales bar graph
    const userSalesBarGraph = (flattened || []).filter((n) => n.report_type === 'sales_won_graph').map((n) => ({ ...n, pipeline_count: n.report_count, service_total_value: n.report_value }));
    const { salesStackedUserKeys, salesStackedUsersData } = formatUserSales(userSalesBarGraph);
    // Sales won pie graph
    const salespersonSalesWonPieGraph = (flattened || []).filter((n) => n.report_type === 'sales_won_user').map((n) => ({ ...n, pipeline_count: n.report_count, service_total_value: n.report_value, name: n.user_name, value: parseInt(n.report_value || 0), status: 'Won' }));
    // Sales UTM Medium Pie graph
    const salesUtmMediumPie = (flattened || []).filter((n) => n.report_type === 'sales_won_utm_medium').map((n) => ({ ...n, pipeline_count: n.report_count, name: n.utm_medium_name, value: parseInt(n.report_count || 0) }));
    // Sales won by soruces list
    const salesUTMSource = (flattened || []).filter((n) => n.report_type === 'sales_won_utm_source').map((n) => ({ ...n, pipeline_count: n.report_count, label: n.utm_source_name, value: parseInt(n.report_count || 0) }));
    // Sales won by campaign list
    const salesUTMCampaign = (flattened || []).filter((n) => n.report_type === 'sales_won_utm_campaign').map((n) => ({ ...n, pipeline_count: n.report_count, label: n.utm_campaign, value: `$${parseInt(n.report_value || 0).toLocaleString()} (${n.report_count} Won)` }));
    // Resons lost pie graph
    const reasonsLost = (flattened || []).filter((n) => n.report_type === 'sales_lost_sub_status').map((n) => ({ ...n, pipeline_count: n.report_count, value: parseInt(n.report_count || 0), name: n.sub_status }));
    // Resons lost pie graph
    const salesLostByUTMMedium = (flattened || []).filter((n) => n.report_type === 'sales_lost_utm_medium').map((n) => ({ ...n, pipeline_count: n.report_count, name: n.utm_medium_name, value: parseInt(n.report_count || 0) }));
    // Sales Lost Pie Graph
    const salesLostSalesperson = (flattened || []).filter((n) => n.report_type === 'sales_lost_user').map((n) => ({ ...n, pipeline_count: n.report_count, service_total_value: n.report_value, name: n.user_name, value: parseInt(n.report_value || 0), status: 'Won' }));
    return {
      ...state,
      totalsBarSales: (totalsBarData || []).length ? totalsBarData : null,
      salesStackedUserKeys: (salesStackedUserKeys || []).length ? salesStackedUserKeys : null,
      salesStackedUsersData: (salesStackedUsersData || []).length ? salesStackedUsersData : null,
      salespersonSalesWonPieGraph: (salespersonSalesWonPieGraph || []).length ? salespersonSalesWonPieGraph : null,
      salesUtmMediumPie: (salesUtmMediumPie || []).length ? salesUtmMediumPie : null,
      salesUTMSource: (salesUTMSource || []).length ? salesUTMSource : null,
      salesUTMCampaign: (salesUTMCampaign || []).length ? salesUTMCampaign : null,
      reasonsLost: (reasonsLost || []).length ? reasonsLost : null,
      salesLostByUTMMedium: (salesLostByUTMMedium || []).length ? salesLostByUTMMedium : null,
      salesLostSalesperson: (salesLostSalesperson || []).length ? salesLostSalesperson : null,
    };
  },
  GET_SALES_REPORT_SUMMARY_FAIL: (state, payload) => ({
    ...state,
    totalsBarSales: 'failed',
    salesStackedUserKeys: 'failed',
    salesStackedUsersData: 'failed',
    salespersonSalesWonPieGraph: 'failed',
    salesUtmMediumPie: 'failed',
    salesUTMSource: 'failed',
    salesUTMCampaign: 'failed',
    reasonsLost: 'failed',
    salesLostByUTMMedium: 'failed',
    salesLostSalesperson: 'failed',
  }),
  FETCH_ACTIVE_PIPELINE_GRAPH_DATA_SUCCESS: (state, payload) => {
    return {
      ...state,
      activePipelineGraphData: payload.data || [],
    };
  },

  FETCH_SALES_REPORTS_SUCCESS: (state, payload) => {
    return {
      ...state,
      salesReports: cleanPayload(payload.data),
    };
  },
  RESET_SALES_PAGINATION_BLOCKER: (state, payload) => {
    return {
      ...state,
      reportingSalesDetailsDisablePagination: false,
    };
  },

  GET_SALES_DETAILS_SUCCESS: (state, payload) => {
    let salesDetailsTotalCount = 0;
    let salesDetailsTotalValue = 0;
    if (Array.isArray(payload.data) && payload.data.length) {
      salesDetailsTotalCount = parseInt(payload.data[0].total_row_count);
      salesDetailsTotalValue = parseInt(payload.data[0].total_row_amount);
    }

    return {
      ...state,
      salesDetails: payload.data,
      salesDetailsTotalCount,
      salesDetailsTotalValue,
    };
  },
  GET_PAGINATED_SALES_DETAILS_SUCCESS: (state, payload) => {
    return {
      ...state,
      salesDetails: [...state.salesDetails, ...payload.data],
      reportingSalesDetailsDisablePagination: (payload.data || []).length === 0 ? true : false,
    };
  },
  GET_PAGINATED_LEAD_DETAILS_SUCCESS: (state, payload) => {
    const CURRENT_STATE_IDS = [];
    state.leadsDetails.forEach((item) => {
      CURRENT_STATE_IDS.push(item.id);
    });
    const final = [];
    state.leadsDetails.forEach((item) => {
      if (CURRENT_STATE_IDS.includes(item.id)) {
        final.push(item);
        const indexOfItem = CURRENT_STATE_IDS.indexOf(item.id);
        CURRENT_STATE_IDS.splice(indexOfItem, 1);
      }
    });
    return {
      ...state,
      leadsDetails: [...final, ...payload.data],
    };
  },

  GET_LEAD_DETAILS_SUCCESS: (state, payload) => {
    let reportingLeadsDetailsTotal = 0;
    if (Array.isArray(payload.data) && payload.data.length) {
      reportingLeadsDetailsTotal = parseInt(payload.data[0].total_row_count);
    }
    return { ...state, leadsDetails: payload.data, reportingLeadsDetailsTotal };
  },

  setGraphHoverState: (state, payload) => ({
    ...state,
    graphHoverState: payload,
  }),
});
